/**
 * PublicDemoBootstrap — auto-mints a fresh demo session on mount if
 * the caller is on `demo.trubeaute.com` (or a preview activated via
 * `?demo=1` / `REACT_APP_DEMO_MODE=true`) and doesn't yet have a
 * session_token in localStorage.
 *
 * This is what makes the demo feel "just works": brand/investor
 * lands on the URL, gets an ephemeral demo user seamlessly, and can
 * walk through the full onboarding without ever clicking Sign in.
 *
 * The user row is tagged `is_demo=True` + `expires_at` in Mongo, so
 * the TTL index sweeps it within 24h. `is_test_user()` returns True
 * for these rows → they are automatically invisible to admin metrics.
 */
import { useEffect } from "react";
import { usePublicDemo } from "../context/PublicDemoContext";
import { startPublicDemoSession } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Module-scoped guard so React strict-mode double-mounts don't fire
// two /demo/session calls or race the async token assignment.
// (useRef pattern was tripped by strict mode: cleanup set cancelled=true
// before the mint response arrived, so the token never got stashed.)
let _demoMintInFlight = false;
let _demoMintCompleted = false;

const PublicDemoBootstrap = () => {
  const { isDemoMode, setDemoBootstrapping } = usePublicDemo();
  const { user, checkAuth } = useAuth();

  useEffect(() => {
    if (!isDemoMode) {
      setDemoBootstrapping(false);
      return;
    }
    // Already have a session on this page-load — nothing to mint.
    if (user || _demoMintCompleted) {
      setDemoBootstrapping(false);
      return;
    }
    if (_demoMintInFlight) return;
    _demoMintInFlight = true;
    (async () => {
      try {
        const demoUser = await startPublicDemoSession();
        _demoMintCompleted = true;
        if (typeof window !== "undefined") {
          window.__trubeaute_demo_user_id = demoUser?.user_id || null;
          // Stash session_token in memory for the axios interceptors —
          // sidesteps httpOnly-cookie visibility races under headless
          // Chrome / Safari.
          if (demoUser?.session_token) {
            window.__trubeaute_demo_session_token = demoUser.session_token;
          }
        }
        if (checkAuth) await checkAuth();
      } catch (_e) {
        // Allow retry on next mount if the mint failed transiently.
        _demoMintCompleted = false;
      } finally {
        _demoMintInFlight = false;
        setDemoBootstrapping(false);
      }
    })();
  }, [isDemoMode, user, checkAuth, setDemoBootstrapping]);

  return null;
};

export default PublicDemoBootstrap;
